import { DeviceTypeIds, INetworkInterface, IWirelessNetworkInterface } from '@ncss/models/dist/v3';
import { BLE_SERVICES, DirectConnectBaseDevice, VisibleDevice } from '../../base/directConnectBaseDevice';
import { ByteList } from 'byte-list';
import { GW4BleIncomingMessage, GW4BLEIncomingMessageType } from './messages/gw4BleIncomingMessage';
import { GW4BleOutgoingMessage } from './messages/gw4BleOutgoingMessage';
import { Command, CommandCode } from './messages/payloads/outgoing/command';
import { Payload } from './messages/payloads/payload';
import { AvailableNetworks } from './messages/payloads/incoming/availableNetworks';
import { Info } from './messages/payloads/incoming/info';
import { Status } from './messages/payloads/incoming/status';
import { SyncProgress } from './messages/payloads/incoming/syncProgress';
import { shallowRef, ShallowRef, watch } from 'vue';
import { DirectConnectGateway, InterfaceType, StatusCode, WifiNetwork } from '../gateway';
import { Ack } from './messages/payloads/incoming/ack';
import { ChangeHost } from './messages/payloads/outgoing/changeHost';
import { ChangeNetwork } from './messages/payloads/outgoing/changeNetwork';
import { ChangeWirelessNetwork } from './messages/payloads/outgoing/changeWirelessNetwork';
import { watchUntil } from '@/utils/watchUntil';
import * as colors from '@/theme/colors.json';
import { NcssEncryptionVersion } from 'ncss-encryption';

export class DirectConnectGW4 extends DirectConnectBaseDevice implements DirectConnectGateway {
  static Init() {
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.GW4, DirectConnectGW4);
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.GW4_LITE, DirectConnectGW4);
  }

  get read() {
    return this.info.value?.read;
  }
  get loading() {
    return this._loading.value;
  }
  get remoteTarget() {
    return this.info.value?.remoteTarget;
  }
  get availableNetworks() {
    return this._availableNetworks.value;
  }
  get syncStatus() {
    return this._syncStatus.value;
  }
  get syncProgress() {
    return this._syncProgress.value;
  }
  get forcingCellular() {
    return this.read?.forcingCellular();
  }
  get partying() {
    return this.read?.partyModeEnabled();
  }

  get powerLedColor() {
    const powerLoss = this.read?.gatewayPowerLoss();
    const cloudConnected = this.read?.cloudConnected();
    const partying = this.partying;
    if (powerLoss) {
      return colors.red['500'];
    } else if (partying) {
      return colors.orange['500'];
    } else if (cloudConnected) {
      return colors.blue['500'];
    }
    return colors.green['500'];
  }

  get programmedLedColor() {
    const programmed = this.read?.programmedToProperty();
    const partying = this.partying;
    if (partying) {
      return colors.cyan['500'];
    } else if (programmed) {
      return colors.green['500'];
    }
    return colors.yellow['500'];
  }

  get rfLedColor() {
    return this.partying ? colors.yellow['500'] : 'transparent';
  }

  get cellularLedColor() {
    const cellReachable = this.read?.flags?.pppCloudReachable();
    const cellSignal = this.read ? this.read.flags.cellularSignal() : 0;
    if (this.partying) {
      return colors.violet['500'];
    }
    if (!cellReachable) {
      return 'transparent';
    } else if (cellSignal >= 3) {
      return colors.green['500'];
    } else if (cellSignal >= 2) {
      return colors.yellow['500'];
    } else if (cellSignal >= 0) {
      return colors.red['500'];
    }
    return 'transparent';
  }

  private info: ShallowRef<Pick<Info, 'read' | 'remoteTarget' | 'deviceStats'> | undefined>;

  temperature?: number;
  get powerPinState() {
    return ByteList.GetBit(this._flags || 0, 0);
  }

  protected serviceId = BLE_SERVICES.WALK_BY.ID;
  protected notifyCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.NOTIFY;
  protected writeCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.WRITE;
  protected useEncryption = true;
  protected encryptionKey = NcssEncryptionVersion.GW4;

  private _flags?: number;
  private _loading: ShallowRef<boolean>;
  private _availableNetworks: ShallowRef<WifiNetwork[]>;
  private _syncStatus: ShallowRef<StatusCode | undefined>;
  private _syncProgress: ShallowRef<number | undefined>;

  constructor(device: VisibleDevice, useMetric: boolean) {
    super(device, useMetric);
    this.info = shallowRef();
    this._loading = shallowRef(false);
    this._availableNetworks = shallowRef([]);
    this._syncStatus = shallowRef();
    this._syncProgress = shallowRef();
  }

  protected parseManufacturerData(bytes: ByteList): void {
    this.temperature = bytes.readInt8();
    this._flags = bytes.readByte();
  }

  protected registerMessages(): void {
    this.messageService.registerMessageGroup({
      mask: GW4BLEIncomingMessageType,
      messageClass: GW4BleIncomingMessage,
      handler: (msg: GW4BleIncomingMessage) => {
        if (msg.payload) {
          this.onIncomingPayload(msg.payload);
        }
        this.checkResponse(msg);
      },
    });
  }

  protected onConnected() {
    super.onConnected();
    this._loading.value = true;
    this.requestInfo();
  }

  requestInfo() {
    const msg = new GW4BleOutgoingMessage(this.deviceId);
    msg.payload = new Command(CommandCode.REQUEST_INFO);
    return this.sendMessage<GW4BleIncomingMessage>(msg, {
      match: (res) => res.payload instanceof Ack && res.payload.requestId === msg.payload?.id,
    });
  }

  party(enabled: boolean): Promise<boolean> {
    return this.sendCommand(enabled ? CommandCode.START_THE_PARTY : CommandCode.STOP_THE_PARTY);
  }

  setForceCellular(force: boolean) {
    return this.sendCommand(force ? CommandCode.CELLULAR_FORCE_ENABLE : CommandCode.CELLULAR_FORCE_DISABLE);
  }

  setCellularEnabled(enabled: boolean) {
    return this.sendCommand(enabled ? CommandCode.CELLULAR_ENABLE : CommandCode.CELLULAR_DISABLE);
  }

  setRemoteTarget(info: { host?: string; port?: number }) {
    const msg = new GW4BleOutgoingMessage(this.deviceId);
    msg.payload = new ChangeHost(info.host, info.port);
    return this.sendMessage<GW4BleIncomingMessage>(msg, {
      match: (res) => res.payload instanceof Info && res.payload.requestId === msg.payload?.id,
    }).then((res) => {
      if (!res?.payload) {
        return;
      }
      const info = res?.payload as Info;
      return {
        host: info.remoteTarget?.host,
        port: info.remoteTarget?.port,
      };
    });
  }

  changeNetwork(
    interfaceType: InterfaceType,
    netInterface: IWirelessNetworkInterface | INetworkInterface
  ): Promise<IWirelessNetworkInterface | INetworkInterface | undefined> {
    const msg = new GW4BleOutgoingMessage(this.deviceId);
    const payload = new ChangeNetwork(interfaceType);
    payload.useDHCP = netInterface.dhcp;
    if (!payload.useDHCP) {
      payload.gateway = netInterface.gateway;
      payload.ipAddress = netInterface.address;
      payload.networkMask = netInterface.netmask;
    }
    msg.payload = payload;
    return this.sendMessage<GW4BleIncomingMessage>(msg, {
      match: (res) => res.payload instanceof Info && res.payload.requestId === msg.payload?.id,
      timeOutSeconds: 10,
    }).then((res) => {
      if (!res?.payload) {
        return;
      }
      const info = res?.payload as Info;
      return {
        ...(interfaceType === 'eth' ? info.read?.eth : interfaceType === 'wlan' ? info.read?.wlan : info.read?.ppp),
      };
    });
  }

  forgetWifiNetwork() {
    return this.sendCommand(CommandCode.FORGET_WIFI_NETWORK);
  }

  changeWifiNetwork(ssid: string, password?: string) {
    const msg = new GW4BleOutgoingMessage(this.deviceId);
    const payload = new ChangeWirelessNetwork();
    payload.useDHCP = true;
    payload.ssid = ssid;
    payload.password = password;
    msg.payload = payload;
    return this.sendMessage<GW4BleIncomingMessage>(msg, {
      match: (res) => res.payload instanceof Info && res.payload.requestId === msg.payload?.id,
    }).then((res) => !!res);
  }

  async forceSync(): Promise<{ success: boolean }> {
    const success = await this.sendCommand(CommandCode.FORCE_SYNC);
    if (!success) {
      return { success };
    }
    return new Promise((resolve) => {
      watchUntil(
        () => this._syncStatus.value,
        (done, val) => {
          if (val === StatusCode.SYNC_STOPPED) {
            done();
            resolve({ success: true });
          } else if (val === StatusCode.SYNC_FAILED) {
            done();
            resolve({ success: false });
          }
        },
        {
          timeoutSeconds: 30,
          onTimeout: () => {
            resolve({ success: false });
          },
        }
      );
    });
  }

  private sendCommand(cmd: CommandCode) {
    const msg = new GW4BleOutgoingMessage(this.deviceId);
    msg.payload = new Command(cmd);
    return this.sendMessage<GW4BleIncomingMessage>(msg, {
      match: (res) => res.payload instanceof Ack && res.payload.requestId === msg.payload?.id,
    }).then((res) => !!res);
  }

  private onIncomingPayload(payload: Payload) {
    if (payload instanceof AvailableNetworks) {
      this.onAvailableNetworks(payload);
    } else if (payload instanceof Info) {
      this.onInfo(payload);
    } else if (payload instanceof Status) {
      this.onStatus(payload);
    } else if (payload instanceof SyncProgress) {
      this.onSyncProgress(payload);
    }
  }

  private onAvailableNetworks(available: AvailableNetworks) {
    this._availableNetworks.value = available.networks;
  }

  private onInfo(info: Info) {
    this._loading.value = false;
    this.info.value = {
      read: info.read,
      deviceStats: info.deviceStats,
      remoteTarget: info.remoteTarget,
    };
  }

  private onStatus(status: Status) {
    this._syncStatus.value = status.code;
  }

  private onSyncProgress(syncProgress: SyncProgress) {
    this._syncProgress.value = syncProgress.progress;
  }
}
